/**
 * The admin API client.
 *
 * Separate from the player app's client on purpose — it speaks to `/api/admin`
 * only, and it carries the admin cookie, which in production lives on a
 * different hostname entirely. Nothing here should ever be imported into the
 * player bundle or vice versa.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/admin${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  })

  const text = await res.text()
  const body = text ? (JSON.parse(text) as unknown) : null

  if (!res.ok) {
    const payload = body as { error?: string; code?: string } | null
    throw new ApiError(payload?.error ?? `Request failed (${res.status})`, res.status, payload?.code)
  }
  return body as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
}

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

export interface Overview {
  users: { total: number; new24h: number; active7d: number }
  opens: {
    total: number
    last24h: number
    stakedCents: number
    returnedCents: number
    staked24hCents: number
    returned24hCents: number
    realisedRtp: number | null
    marginCents: number
  }
  liability: {
    balancesCents: number
    vaultCents: number
    positions: number
    outstandingCents: number
  }
  tiers: Array<{ tier: string; label: string; opens: number; stakedCents: number }>
  market: {
    refreshedAt: string
    minMarketCap: number
    drawable: number
    belowFloor: number
    ageHours: number
    source: string | null
  } | null
}

export interface AdminUserRow {
  id: string
  address: string
  username: string | null
  profilePublic: boolean
  balanceCents: number
  stakedCents: number
  returnedCents: number
  realisedRtp: number | null
  opens: number
  positions: number
  createdAt: string
  lastSeenAt: string
}

export interface AdminUserDetail {
  user: AdminUserRow & {
    bio: string | null
    avatarKind: string
    avatarRef: string | null
    lockedCents: number
    referralCode: string | null
    referredById: string | null
  }
  opens: Array<{
    id: string
    packId: string
    packName: string
    priceCents: number
    totalCents: number
    multiple: number
    best: string
    nonce: number
    openedAt: string
  }>
  positions: Array<{
    id: string
    symbol: string
    kind: string
    name: string
    quantity: number
    rarity: string
    basisCents: number
    valueCents: number
    acquiredAt: string
  }>
  vaultValueCents: number
  ledger: Array<{
    id: string
    kind: string
    amountCents: number
    balanceAfterCents: number
    memo: string | null
    ref: string | null
    createdAt: string
  }>
  seeds: Array<{
    id: string
    serverSeedHash: string
    clientSeed: string
    nonce: number
    active: boolean
    createdAt: string
    retiredAt: string | null
  }>
  deposits: Array<{ id: string; txHash: string; amountCents: number; status: string; seenAt: string }>
  withdrawals: Array<{
    id: string
    toAddress: string
    amountCents: number
    status: string
    txHash: string | null
    failure: string | null
    requestedAt: string
  }>
}

export interface AdminOpen {
  id: string
  packId: string
  packName: string
  priceCents: number
  totalCents: number
  multiple: number
  best: string
  openedAt: string
  user: { id: string; username: string | null; address: string }
  topPull: { symbol: string; rarity: string; valueCents: number } | null
}

export interface Settlement {
  enabled: boolean
  token: { symbol: string; decimals: number } | null
  totals: { depositedCents: number; withdrawnCents: number }
  chain: {
    treasuryRaw: string
    hotRaw: string
    reconciliation: {
      creditedCents: number
      withdrawnCents: number
      inFlightCents: number
      expectedTreasuryRaw: string
      actualTreasuryRaw: string
      differenceRaw: string
      ok: boolean
    } | null
    error: string | null
  } | null
  pendingWithdrawals: Array<{
    id: string
    amountCents: number
    toAddress: string
    status: string
    failure: string | null
    requestedAt: string
    user: { id: string; username: string | null; address: string }
  }>
  withdrawals: Array<{
    id: string
    amountCents: number
    toAddress: string
    status: string
    txHash: string | null
    requestedAt: string
    user: { id: string; username: string | null }
  }>
  unattributed: Array<{
    id: string
    txHash: string
    fromAddress: string
    amountCents: number
    seenAt: string
  }>
  deposits: Array<{
    id: string
    txHash: string
    fromAddress: string
    amountCents: number
    status: string
    seenAt: string
  }>
}

export interface AuditEntry {
  id: string
  action: string
  subject: string | null
  detail: string | null
  ip: string | null
  createdAt: string
}
